// Global Instructions Rule Applied!

import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { fetchFundDashboardDetail } from '../services/fundsService.ts'
import {
  setActiveRecord,
  registerRecordUsage,
  unregisterRecordUsage,
  selectActiveRecordById
} from '../../../state_manager/state_update_actions/active_records/index.ts'
import { selectFundById } from '../../../state_manager/state_update_actions/funds/index.ts'
import { updateFundRecord } from '../../../state_manager/state_update_actions/funds/index.ts'

/**
 * useFundDashboard — fetches the fund's dashboard detail for the Full form.
 * Mirrors the Companies Full-form lifecycle:
 *   - on first mount (or recordId change) check activeRecords; if missing,
 *     fetch via the service and dispatch setActiveRecord + registerRecordUsage.
 *   - on unmount or recordId change, unregisterRecordUsage so activeRecords
 *     can clean up.
 *
 * Per-company investment data lives in {@link useFundInvestmentSummary}.
 */
export function useFundDashboard(fundId: string | null | undefined) {
  const dispatch = useDispatch()
  const recordIdString = fundId ? String(fundId) : null

  // Read from activeRecords first; fall back to the funds slice. This mirrors
  // the read chain used by MainTabContainer for tab titles and lets the
  // dashboard render whatever's already cached before any new fetch lands.
  const fromActiveRecords = useSelector(
    (state: any) => (recordIdString ? selectActiveRecordById(state, recordIdString) : undefined)
  )
  const fromFundsSlice = useSelector(
    (state: any) => (recordIdString ? selectFundById(state, recordIdString) : undefined)
  )
  const detail: Record<string, unknown> | null =
    (fromActiveRecords as Record<string, unknown> | undefined) ??
    (fromFundsSlice as Record<string, unknown> | undefined) ??
    null

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const hasAttemptedFetch = useRef(false)

  // Reset fetch flag whenever the recordId changes.
  useEffect(() => {
    hasAttemptedFetch.current = false
  }, [recordIdString])

  // Fetch detail. Cached via setActiveRecord + updateFundRecord.
  useEffect(() => {
    if (!recordIdString) {
      setLoading(false)
      setError(null)
      return
    }

    if (hasAttemptedFetch.current) return

    let cancelled = false
    hasAttemptedFetch.current = true

    const run = async () => {
      setLoading(true)
      setError(null)
      try {
        const accessToken = getAccessToken(store.getState().auth)
        const detailRes = await fetchFundDashboardDetail(recordIdString, accessToken)
        if (cancelled) return

        if (detailRes.error) {
          setError(detailRes.error)
        }

        if (detailRes.data) {
          dispatch(setActiveRecord({ uuid: recordIdString, record: detailRes.data }))
          dispatch(registerRecordUsage({ uuid: recordIdString, context: 'fullForm' }))
          // Also keep the funds slice in sync so list-driven readers see fresh data.
          dispatch(updateFundRecord({ fundId: recordIdString, updates: detailRes.data as Record<string, any> }))
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load fund')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [recordIdString, dispatch])

  // Cleanup activeRecords usage on unmount / recordId change.
  useEffect(() => {
    if (!recordIdString) return
    return () => {
      dispatch(unregisterRecordUsage({ uuid: recordIdString, context: 'fullForm' }))
    }
  }, [recordIdString, dispatch])

  return { detail, loading, error }
}
